import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useDraggable } from '@dnd-kit/core'
import API from '../../../../services/API'
import { COLOURS, KIND, SOURCE, paletteId } from './chipTypes'

const consumableKey = (c) => `${c.name}|${c.size || ''}`

// A draggable palette entry. `kind` decides the payload shape the canvas
// receives on drop.
export const PaletteChip = ({ id, kind, payload, label, sub, disabled }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled,
    data: { source: SOURCE.PALETTE, kind, payload },
  })
  const isAsset = kind !== KIND.CONSUMABLE
  const isWildcard = kind === KIND.ASSET_WILDCARD

  return (
    <Stack
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      direction="row"
      alignItems="center"
      gap={0.5}
      sx={{
        px: 0.5,
        py: 0.4,
        borderRadius: '5px',
        border: '1px solid',
        borderStyle: isWildcard ? 'dashed' : 'solid',
        borderColor: isAsset ? COLOURS.assetBorder : COLOURS.divider,
        bgcolor: isAsset ? COLOURS.assetBg : 'background.paper',
        opacity: isDragging ? 0.4 : 1,
        cursor: disabled ? 'default' : 'grab',
        touchAction: 'none',
        userSelect: 'none',
        '&:hover': disabled ? {} : { borderColor: COLOURS.teal },
      }}
    >
      {!disabled && (
        <DragIndicatorIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
      )}
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontSize: '0.78rem',
          fontStyle: isWildcard ? 'italic' : 'normal',
          minWidth: 0,
        }}
      >
        {label}
        {sub ? (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {' '}
            · {sub}
          </Box>
        ) : null}
      </Typography>
    </Stack>
  )
}

const GroupHeader = ({ title, count, open, onToggle }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    onClick={onToggle}
    sx={{ cursor: 'pointer', py: 0.5 }}
  >
    <Typography
      variant="overline"
      sx={{ letterSpacing: 0.6, color: COLOURS.navy, lineHeight: 1.6 }}
    >
      {title}{' '}
      <Box component="span" sx={{ color: 'text.disabled' }}>
        ({count})
      </Box>
    </Typography>
    {open ? (
      <ExpandMoreIcon fontSize="small" />
    ) : (
      <ChevronRightIcon fontSize="small" />
    )}
  </Stack>
)

const Palette = ({ readOnly, onAddConsumable, setToast }) => {
  const [search, setSearch] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [extraSuggestions, setExtraSuggestions] = useState([])
  const [taxonomy, setTaxonomy] = useState({ categories: [], misfiled: [] })
  const [openConsumables, setOpenConsumables] = useState(true)
  const [openAssets, setOpenAssets] = useState(true)
  const [collapsedCats, setCollapsedCats] = useState({})
  const [newOpen, setNewOpen] = useState(false)
  const [draft, setDraft] = useState({ name: '', size: '', noExpiry: false })
  const [fixing, setFixing] = useState(null)

  const fetchTaxonomy = useCallback(async () => {
    const res = await API.get('/makeready/asset-taxonomy')
    if (res.status === 200 && res.data) {
      setTaxonomy({
        categories: Array.isArray(res.data.categories)
          ? res.data.categories
          : [],
        misfiled: Array.isArray(res.data.misfiled) ? res.data.misfiled : [],
      })
    }
  }, [])

  useEffect(() => {
    API.get('/makeready/consumable-suggestions').then((res) => {
      if (res.status === 200 && Array.isArray(res.data))
        setSuggestions(res.data)
    })
    fetchTaxonomy()
  }, [fetchTaxonomy])

  const q = search.trim().toLowerCase()
  const matches = (...parts) =>
    !q ||
    parts.some((p) =>
      String(p || '')
        .toLowerCase()
        .includes(q),
    )

  const allConsumables = useMemo(() => {
    const seen = new Set()
    return [...extraSuggestions, ...suggestions].filter((c) => {
      const key = consumableKey(c)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [suggestions, extraSuggestions])

  const visibleConsumables = allConsumables.filter((c) =>
    matches(c.name, c.size),
  )

  const visibleCategories = taxonomy.categories
    .map((cat) => ({
      ...cat,
      subCategories: (cat.subCategories || []).filter((s) =>
        matches(cat.category, s),
      ),
    }))
    .filter((cat) => matches(cat.category) || cat.subCategories.length > 0)

  const submitNew = () => {
    const name = draft.name.trim()
    if (!name) return
    const payload = {
      name,
      size: draft.size.trim(),
      noExpiry: !!draft.noExpiry,
      quantity: 1,
    }
    setExtraSuggestions((prev) => [payload, ...prev])
    onAddConsumable?.(payload)
    setDraft({ name: '', size: '', noExpiry: false })
    setNewOpen(false)
  }

  const fixMisfiled = async (asset) => {
    setFixing(asset._id)
    try {
      const res = await API.put(`/asset/${asset._id}`, {
        _id: asset._id,
        category: asset.suggestedCategory,
        subCategory: asset.subCategory,
      })
      if (res.status === 200) {
        setToast?.({
          message: `${asset.assetID} moved to ${asset.suggestedCategory} › ${asset.subCategory}`,
        })
        await fetchTaxonomy()
      } else {
        setToast?.({
          message:
            res.data?.duplicateError ||
            res.data?.error ||
            `Unable to move ${asset.assetID}`,
          severity: 'error',
        })
      }
    } catch {
      setToast?.({
        message: `Unable to move ${asset.assetID}`,
        severity: 'error',
      })
    } finally {
      setFixing(null)
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: COLOURS.divider,
        borderRadius: '5px',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
      }}
    >
      <TextField
        size="small"
        placeholder="Search items…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      {readOnly ? null : (
        <Typography variant="caption" color="text.secondary">
          Drag an item onto a section, or use "New consumable" to add it to the
          selected section.
        </Typography>
      )}

      {/* Consumables */}
      <GroupHeader
        title="Consumables"
        count={visibleConsumables.length}
        open={openConsumables}
        onToggle={() => setOpenConsumables((o) => !o)}
      />
      <Collapse in={openConsumables}>
        <Stack gap={0.75}>
          {visibleConsumables.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              {q
                ? 'No consumables match your search.'
                : 'No consumables suggested yet. Add one below.'}
            </Typography>
          )}
          {visibleConsumables.map((c) => (
            <PaletteChip
              key={consumableKey(c)}
              id={paletteId(KIND.CONSUMABLE, consumableKey(c))}
              kind={KIND.CONSUMABLE}
              payload={{
                name: c.name,
                size: c.size || '',
                noExpiry: !!c.noExpiry,
                quantity: 1,
              }}
              label={c.name}
              sub={c.size}
              disabled={readOnly}
            />
          ))}
          {!readOnly && !newOpen && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setNewOpen(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              New consumable
            </Button>
          )}
          {!readOnly && newOpen && (
            <Stack
              gap={1}
              sx={{
                p: 1,
                border: '1px dashed',
                borderColor: COLOURS.teal,
                borderRadius: '5px',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submitNew()
                }
              }}
            >
              <TextField
                size="small"
                label="Name"
                autoFocus
                value={draft.name}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, name: e.target.value }))
                }
                required
              />
              <TextField
                size="small"
                label="Size / variant"
                value={draft.size}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, size: e.target.value }))
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={draft.noExpiry}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, noExpiry: e.target.checked }))
                    }
                  />
                }
                label={<Typography variant="body2">No expiry date</Typography>}
              />
              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button size="small" onClick={() => setNewOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!draft.name.trim()}
                  onClick={submitNew}
                >
                  Add
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Collapse>

      <Divider sx={{ my: 0.5 }} />

      {/* Expected assets */}
      <GroupHeader
        title="Expected assets"
        count={visibleCategories.length}
        open={openAssets}
        onToggle={() => setOpenAssets((o) => !o)}
      />
      <Collapse in={openAssets}>
        <Stack gap={1}>
          {visibleCategories.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              {q
                ? 'No asset categories match your search.'
                : 'No asset categories found. Add assets in Asset Manager first.'}
            </Typography>
          )}
          {visibleCategories.map((cat) => {
            const collapsed = !!collapsedCats[cat.category]
            return (
              <Box key={cat.category}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setCollapsedCats((p) => ({
                        ...p,
                        [cat.category]: !collapsed,
                      }))
                    }
                    sx={{ p: 0.25 }}
                    aria-label={collapsed ? 'Expand' : 'Collapse'}
                  >
                    {collapsed ? (
                      <ChevronRightIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <PaletteChip
                      id={paletteId(KIND.ASSET_WILDCARD, cat.category)}
                      kind={KIND.ASSET_WILDCARD}
                      payload={{
                        category: cat.category,
                        subCategory: '',
                        quantity: 1,
                      }}
                      label={`Any ${cat.category}`}
                      sub={
                        typeof cat.assetCount === 'number'
                          ? `${cat.assetCount}`
                          : undefined
                      }
                      disabled={readOnly}
                    />
                  </Box>
                </Stack>
                <Collapse in={!collapsed}>
                  <Stack gap={0.5} sx={{ pl: 3.5, pt: 0.5 }}>
                    {cat.subCategories.map((sub) => (
                      <PaletteChip
                        key={`${cat.category}|${sub}`}
                        id={paletteId(KIND.ASSET, `${cat.category}|${sub}`)}
                        kind={KIND.ASSET}
                        payload={{
                          category: cat.category,
                          subCategory: sub,
                          quantity: 1,
                        }}
                        label={sub}
                        disabled={readOnly}
                      />
                    ))}
                    {cat.subCategories.length === 0 && (
                      <Typography variant="caption" color="text.disabled">
                        No sub-categories
                      </Typography>
                    )}
                  </Stack>
                </Collapse>
              </Box>
            )
          })}
        </Stack>
      </Collapse>

      {taxonomy.misfiled.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.25,
            border: '1px solid #F28B1F',
            bgcolor: 'rgba(242,139,31,0.08)',
            borderRadius: '5px',
          }}
        >
          <Stack direction="row" alignItems="center" gap={0.75} mb={0.5}>
            <WarningAmberIcon sx={{ color: '#F28B1F' }} fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>
              {taxonomy.misfiled.length} asset
              {taxonomy.misfiled.length === 1 ? '' : 's'} look
              {taxonomy.misfiled.length === 1 ? 's' : ''} mis-filed
            </Typography>
          </Stack>
          <Stack gap={1}>
            {taxonomy.misfiled.map((a) => (
              <Box key={a._id}>
                <Typography variant="caption" display="block">
                  <b>{a.assetID}</b> is a {a.subCategory} but sits under{' '}
                  {a.category}, so it will never match a {a.subCategory} slot.
                </Typography>
                {!readOnly && a.suggestedCategory && (
                  <Button
                    size="small"
                    disabled={fixing === a._id}
                    onClick={() => fixMisfiled(a)}
                    sx={{ px: 0.5, minWidth: 0, fontSize: '0.72rem' }}
                  >
                    Move to {a.suggestedCategory} › {a.subCategory}
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Paper>
  )
}

export default Palette
